/**
 * This file provides helpers for mutating a list of paginated items.
 * Should be used in settings.
 *
 * This helper provides a way to handle optimistic updates.
 * To implement a mutation, simply implement the interface ListMutation,
 * and use it in usePersistedListMutation in ./resourceTableHooks.ts
 */

import {keyBy} from 'lodash';
import {useMemo} from 'react';

export interface MutationActionLike<TPayload = unknown> {
  type: string;
  payload: TPayload;
  pageIndex?: number;
}

type RunSideEffect<TPayload, TMutationResult> = (
  /** The payload of the action */
  payload: TPayload,
) => Promise<TMutationResult>;

type RunOptimisticUpdate<TData, TPayload> = (
  /** The data to optimistically update */
  data: TData,

  /** The payload of the action */
  payload: TPayload,

  /** The page index to run the optimistic update on */
  pageIndex: number,
) => TData;

type RunCacheReconciliation<TData, TSideEffectResult> = (
  /** The data to optimistically update */
  data: TData,

  /** The data returned by runSideEffect */
  sideEffectResult: TSideEffectResult,

  /** The page index to run the cache reconciliation on */
  pageIndex: number,
) => TData;

/**
 * ListMutation is an interface to implement a mutation on a list.
 * Is used with React Query.
 *
 * @param payload is data related to a mutation. Ex: the id of the updated item.
 */
export interface ListMutation<
  TData /** Data type of a page */,
  TMutationAction extends MutationActionLike /** Redux style action */,
  TSideEffectResult /** Once side effect is done, the type of data returned. This result will be reinjected in runCacheReconciliation */,
> {
  /**
   * Key of the mutation.
   */
  mutationKey: TMutationAction['type'];

  /**
   * runSideEffect should update the data on the server.
   * @returns Can return a result to perform cache reconciliation in runCacheReconciliation method.
   */
  runSideEffect: RunSideEffect<TMutationAction['payload'], TSideEffectResult>;

  /**
   * runOptimisticUpdate should optimistically update the data in the React Query cache.
   * If runSideEffect fails, cache will be set to previous state.
   */
  runOptimisticUpdate: RunOptimisticUpdate<TData, TMutationAction['payload']>;

  /**
   * runCacheReconciliation should update the cache with the data returned by API.
   */
  runCacheReconciliation?: RunCacheReconciliation<TData, TSideEffectResult>;
}

/**
 * useListMutations
 *
 * This hook is responsible for dispatching actions to the corresponding mutation instance.
 * Is used by React Query.
 *
 * @param listMutations Instances of ListMutation.
 */
export function useListMutations<
  TData,
  TMutationAction extends MutationActionLike,
  TListMutations extends ReadonlyArray<ListMutation<TData, TMutationAction, any>>,
>(listMutations: TListMutations) {
  const mutationInstances = useMemo(
    () => keyBy(listMutations, (mutation) => mutation.mutationKey),
    [listMutations],
  );

  return useMemo(
    () => ({
      applySideEffect: <TMutation extends TMutationAction, TReturn>(mutation: TMutation): Promise<TReturn> =>
        mutationInstances[mutation.type].runSideEffect(mutation.payload),

      updateOptimistically: <TMutation extends TMutationAction>(
        data: TData,
        mutation: TMutation,
        pageIndex: number,
      ): TData => mutationInstances[mutation.type].runOptimisticUpdate(data, mutation.payload, pageIndex),

      reconciliateCache: <TMutation extends TMutationAction, TMutationResult>(
        data: TData,
        mutation: TMutation,
        result: TMutationResult,
        pageIndex: number,
      ): TData => {
        const mutationInstance = mutationInstances[mutation.type];

        if (!mutationInstance.runCacheReconciliation) {
          return data;
        }

        return mutationInstance.runCacheReconciliation(data, result, pageIndex);
      },
    }),

    [mutationInstances],
  );
}
